import type {
  MemberDetails,
  ReactionDetails,
  RepliesResponse,
} from '@assembly-web/services';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { defineMessages, useIntl } from 'react-intl';

import { Avatar, AvatarSize } from '../../../DesignSystem/Feedback/Avatar';
import { Tooltip } from '../../../DesignSystem/Feedback/Tooltip';
import { AvatarGroup } from '../AvatarGroup';
import { ReactionEmoji } from '../Reactions/ReactionEmoji';
import { ReactionPicker } from '../Reactions/ReactionPicker';
import { DummyCurrentMember } from './data/flowsData';
import { PostCardFooterHiddenState } from './PostCardFooterHiddenState';
import { RoundedButton } from './RoundedButton';
import { getTimeAgo } from './utils';

const messages = defineMessages({
  viewReplies: {
    defaultMessage: 'View replies',
    id: 'hvCo0f',
  },
  lastRepliedAt: {
    defaultMessage: 'Last reply {timeAgo} ago',
    id: 'zG0jzb',
  },
  repliesTurnedOff: {
    defaultMessage: 'Replies have been turned off',
    id: 'h9h1y6',
  },
  repliesAndReactionsTurnedOff: {
    defaultMessage: 'Replies and reactions have been turned off',
    id: '7BL80V',
  },
});

export function PostCardFooter({
  reactions,
  repliesData,
  onReactionClick,
  onReplyButtonClick,
  currentMember,
  hideReactions,
  hideReplies,
}: {
  reactions: ReactionDetails[];
  repliesData: RepliesResponse;
  onReactionClick: (reaction: ReactionDetails) => void;
  onReplyButtonClick: () => void;
  currentMember: MemberDetails;
  hideReactions: boolean;
  hideReplies: boolean;
}) {
  const {
    count: commentsCount,
    initialRespondents,
    respondentsCount,
    lastRepliedAt = '',
  } = repliesData;

  const { formatMessage } = useIntl();

  const { memberID: currentMemberId, image, name } = currentMember;

  const getTooltipText = (reaction: ReactionDetails) => {
    const { members } = reaction;
    const memberNames = members
      .map((member, index) => {
        const currentMemberText = index === 0 ? 'You' : 'you';
        return currentMember.memberID === member.memberID
          ? currentMemberText
          : member.name;
      })
      .join(', ');
    return `${memberNames} reacted with this emoji.`;
  };

  if (hideReactions && hideReplies) {
    return (
      <PostCardFooterHiddenState
        name={name}
        image={image}
        currentMemberId={currentMemberId}
        text={formatMessage(messages.repliesAndReactionsTurnedOff)}
      />
    );
  }

  return (
    <section className="pt-2">
      {!hideReactions && (
        <div className="flex flex-wrap gap-2">
          {reactions.map((reaction) => {
            const isSelected = reaction.members.some(
              (i) => i.memberID === currentMemberId
            );

            return (
              <Tooltip
                key={reaction.name}
                tooltipText={getTooltipText(reaction)}
              >
                <div>
                  <RoundedButton
                    onClick={() => onReactionClick(reaction)}
                    isSelected={isSelected}
                  >
                    <ReactionEmoji
                      reaction={reaction}
                      size={20}
                      count={reaction.members.length}
                      isSelected={isSelected}
                    />
                  </RoundedButton>
                </div>
              </Tooltip>
            );
          })}
          <ReactionPicker
            onReactionClick={(reaction: Omit<ReactionDetails, 'members'>) => {
              onReactionClick({
                ...reaction,
                members: [
                  {
                    ...DummyCurrentMember,
                    memberID: currentMemberId,
                  },
                ],
              });
            }}
          />
        </div>
      )}
      {hideReplies ? (
        <PostCardFooterHiddenState
          name={name}
          image={image}
          currentMemberId={currentMemberId}
          text={formatMessage(messages.repliesTurnedOff)}
        />
      ) : (
        <button
          onClick={onReplyButtonClick}
          className="group mt-2 flex w-full items-center gap-2 rounded border border-transparent p-1 hover:border-primary-6 hover:bg-primary-1"
        >
          <div className="flex">
            {respondentsCount > 0 ? (
              <AvatarGroup
                avatars={initialRespondents}
                size={AvatarSize.Medium}
                limit={5}
                totalCount={respondentsCount}
              />
            ) : (
              <Avatar
                size={AvatarSize.Medium}
                image={image}
                name={name}
                memberID={currentMemberId}
                tooltipText={name}
              />
            )}
          </div>

          <div className="flex w-full flex-1 items-center justify-between">
            <span className="flex gap-2 text-sm text-primary-6">
              {commentsCount
                ? `${commentsCount} ${
                    commentsCount === 1 ? 'reply' : 'replies'
                  }`
                : 'Reply to this post'}
              {commentsCount > 0 && lastRepliedAt !== '' && (
                <>
                  <span className="w-84 hidden text-left text-sm text-primary-6 group-hover:inline">
                    {formatMessage(messages.viewReplies)}
                  </span>
                  <span className="w-84 text-left text-sm text-gray-8 group-hover:hidden">
                    {formatMessage(messages.lastRepliedAt, {
                      timeAgo: getTimeAgo(new Date(lastRepliedAt)),
                    })}
                  </span>
                </>
              )}
            </span>
            <div className="flex">
              <ChevronRightIcon className="hidden h-4 text-primary-6 group-hover:inline" />
            </div>
          </div>
        </button>
      )}
    </section>
  );
}
